import {
  Area, Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import Skeleton from "@components/scheduling/analytics/Skeleton";
import { formatCount, successesOf } from "@components/scheduling/analytics/format";
import {
  AREA_FILL_OPACITY, BAR_MAX_WIDTH, BAR_RADIUS, CHROME, COLORS, LINE_WIDTH,
} from "@components/scheduling/analytics/chartTheme";

/** The tooltip's rows: the bar's two parts last, each a plain count (missing reads 0). */
const TOOLTIP_ROWS = [
  ["calls", "Calls"],
  ["answered", "Answered"],
  ["booked", "Booked"],
  ["transferred", "Transferred"],
];

/** "2026-09-29" -> "Sep 29", read as a calendar date, never shifted by the viewer's zone. */
export function formatDay(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!match) return String(value || "");
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-sm">
      <p className="mb-1 font-medium text-gray-900">{formatDay(label)}</p>
      {TOOLTIP_ROWS.map(([key, name]) => (
        <p key={key} className="flex justify-between gap-4 text-gray-600">
          <span>{name}</span>
          <span className="font-medium text-gray-900">{formatCount(row[key])}</span>
        </p>
      ))}
    </div>
  );
}

function sum(rows, key) {
  return rows.reduce((acc, row) => acc + (Number(row?.[key]) || 0), 0);
}

/** Each day with its successes filled in, so the bar reads older responses too. */
function withSuccesses(rows) {
  return rows.map((row) => ({ ...row, successes: successesOf(row) }));
}

/**
 * Calls, answered and successes per day. Calls is context (a slate wash);
 * answered is the line a reader follows; booked + transferred is the bar
 * that matters. One axis: the three share a count scale.
 */
export default function TrendChart({ daily, loading = false }) {
  const rows = withSuccesses(Array.isArray(daily) ? daily : []);

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4" aria-busy="true">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="mt-4 h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="text-sm font-medium text-gray-900">Daily calls</h3>
      {rows.length === 0 ? (
        <div className="mt-4 flex h-64 items-center justify-center rounded-lg border border-dashed border-gray-300">
          <p className="text-sm text-gray-500">No calls in this period</p>
        </div>
      ) : (
        <div
          data-testid="trend-chart"
          role="img"
          aria-label={`Daily trend over ${rows.length} days: ${formatCount(sum(rows, "calls"))} calls, `
            + `${formatCount(sum(rows, "answered"))} answered, `
            + `${formatCount(sum(rows, "successes"))} booked or transferred`}
          className="mt-2 h-64 w-full"
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid vertical={false} stroke={CHROME.grid} strokeWidth={1} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDay}
                tick={{ fill: CHROME.tick, fontSize: 11 }}
                axisLine={{ stroke: CHROME.axis }}
                tickLine={false}
                minTickGap={24}
              />
              <YAxis
                allowDecimals={false}
                tickFormatter={formatCount}
                tick={{ fill: CHROME.tick, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={56}
              />
              <Tooltip content={<TrendTooltip />} cursor={{ stroke: CHROME.axis }} />
              <Legend iconType="plainline" iconSize={12}
                      formatter={(value) => <span className="text-gray-600">{value}</span>}
                      wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Area type="monotone" dataKey="calls" name="Calls" stroke={COLORS.calls}
                    strokeWidth={LINE_WIDTH} fill={COLORS.calls} fillOpacity={AREA_FILL_OPACITY}
                    isAnimationActive={false} />
              <Line type="monotone" dataKey="answered" name="Answered" stroke={COLORS.answered}
                    strokeWidth={LINE_WIDTH} dot={false}
                    activeDot={{ r: 4, stroke: CHROME.surface, strokeWidth: 2 }}
                    isAnimationActive={false} />
              <Bar dataKey="successes" name="Booked + transferred" fill={COLORS.booked} radius={BAR_RADIUS}
                   maxBarSize={BAR_MAX_WIDTH} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
